import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { top20AccountsService } from '@/services/adminService.js'

/**
 * Extract organization ID from Simplicate URL or direct ID
 * Supports formats:
 * - https://frisseblikken.simplicate.nl/crm/organizations/organization:21ea35b0780c7f46/overview
 * - https://frisseblikken.simplicate.nl/crm/organizations/organization:abc123
 * - organization:abc123
 *
 * @param {string} input - URL or organization ID
 * @returns {string|null} - Extracted organization ID or null if not found
 */
export function extractOrganizationId(input) {
  if (!input) return null

  // If already in format "organization:xxx", extract just the ID part (handle trailing paths)
  if (input.startsWith('organization:')) {
    return input.split('/')[0]
  }

  // Extract from URL pattern - matches "organization:" followed by hex characters
  const match = input.match(/organization:[a-f0-9]+/i)
  return match ? match[0] : null
}

/**
 * Validate if input contains a valid organization ID
 * @param {string} input - URL or organization ID
 * @returns {boolean}
 */
export function isValidUrl(input) {
  return extractOrganizationId(input) !== null
}

/**
 * Composable for managing Top 20 accounts
 */
export function useTop20Accounts() {
  const queryClient = useQueryClient()

  // Query for fetching all Top 20 accounts
  const {
    data: accounts,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['top20-accounts'],
    queryFn: () => top20AccountsService.getAll(),
    staleTime: 1000 * 60 * 5 // 5 minutes
  })

  // Mutation for adding a new account
  const addMutation = useMutation({
    mutationFn: data => top20AccountsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['top20-accounts'] })
      // Also invalidate prospects data so the is_top20 flag gets updated
      queryClient.invalidateQueries({ queryKey: ['prospects-leads'] })
    }
  })

  // Mutation for deleting an account
  const deleteMutation = useMutation({
    mutationFn: id => top20AccountsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['top20-accounts'] })
      queryClient.invalidateQueries({ queryKey: ['prospects-leads'] })
    }
  })

  return {
    accounts,
    isLoading,
    error,
    refetch,
    addAccount: addMutation.mutateAsync,
    deleteAccount: deleteMutation.mutateAsync,
    isAdding: addMutation.isPending,
    isDeleting: deleteMutation.isPending,
    extractOrganizationId,
    isValidUrl
  }
}
